import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Send, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TOPICS = [
  { key: 'Payment', color: '#D4AF37' },
  { key: 'Planning', color: '#3B82F6' },
  { key: 'Contract', color: '#8B5CF6' },
  { key: 'General', color: '#6B7280' },
];

// Polling / presence cadence. Chosen so the widget feels live without
// pounding the endpoint: presence 30s so the away-detector (60s) still
// has one miss of slack; unread 15s so a new reply feels near-instant.
const UNREAD_POLL_MS = 15000;
const PRESENCE_PING_MS = 30000;

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function ClientMessageWidget({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [topic, setTopic] = useState('General');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Unread count + latest snippet come from the lightweight
  // `/messages/client/{token}/unread-count` endpoint. Keeping the count in
  // its own state (not derived from `messages`) means the badge updates
  // even while the widget is closed — we don't need to load the full
  // thread just to know one new message landed.
  const [unreadCount, setUnreadCount] = useState(0);
  // Tracks which messageId we've already shown a toast for so a
  // still-visible unread message doesn't re-toast every 15s.
  const toastedIdRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // The full-thread endpoint also clears readByClient AND deletes the
      // pending email-notify batch on the server, so opening the widget is
      // the single "I've seen this" action from the client side.
      const res = await fetch(`${API}/api/messages/client/${token}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setUnreadCount(0);
        toastedIdRef.current = null;
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  // Lightweight background poll — count only, no side-effects. Never touches
  // readByClient so opening the widget stays the ONE user gesture that
  // marks the batch read.
  const pollUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/messages/client/${token}/unread-count`);
      if (!res.ok) return;
      const data = await res.json();
      const count = data.unreadCount || 0;
      setUnreadCount(count);

      // Slide-up preview toast — fires ONCE per new message id, and only
      // when the widget is closed (an open widget already shows the reply).
      const latestId = data.latest?.messageId;
      if (
        count > 0 &&
        latestId &&
        latestId !== toastedIdRef.current &&
        !open
      ) {
        toastedIdRef.current = latestId;
        const sender = data.latest?.senderName || 'Team';
        const snippet = (data.latest?.snippet || '').trim();
        toast(
          `${sender} replied${snippet ? `: ${snippet.length > 80 ? snippet.slice(0, 80) + '…' : snippet}` : ''}`,
          {
            duration: 5000,
            action: {
              label: 'Open',
              onClick: () => setOpen(true),
            },
            className: 'client-message-toast',
          }
        );
      }
    } catch { /* silent */ }
  }, [token, open]);

  useEffect(() => { if (open) fetchMessages(); }, [open, fetchMessages]);

  // Presence heartbeat — pings `/leads/client-view/{token}/ping` every 30s
  // while the tab is visible. The backend uses "no ping in 60s" as the
  // away-signal for the debounced-email scheduler, so this cadence gives
  // us one miss of slack without spamming the endpoint. Suspending the
  // ping when the tab is hidden means backgrounded tabs are correctly
  // treated as away — mail delivers as expected.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const ping = async () => {
      if (cancelled || (typeof document !== 'undefined' && document.hidden)) return;
      try {
        await fetch(`${API}/api/leads/client-view/${token}/ping`, { method: 'POST' });
      } catch { /* silent */ }
    };
    ping();
    const id = setInterval(ping, PRESENCE_PING_MS);
    const onVis = () => { if (!document.hidden) ping(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [token]);

  // Unread poll — same visibility discipline so a background tab doesn't
  // silently drain data.
  useEffect(() => {
    if (!token) return undefined;
    pollUnread();
    const id = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) pollUnread();
    }, UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, [token, pollUnread]);

  // Deep-links — used by the "View conversation" button in the email:
  //   /client-view/{token}?chat=open   (new; email deep-link)
  //   /client-view/{token}#contact     (legacy; payment reminder)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('chat') === 'open' || window.location.hash === '#contact') {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/messages/client/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text.trim(), topic }),
      });
      if (!res.ok) throw new Error();
      setText('');
      setTopic('General');
      toast.success("Message sent — we'll get back to you shortly.");
      fetchMessages();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  // Never sit on top of a sticky bottom action bar. The client stages mount
  // their own fixed bars (.cvm-bar on the mobile screens, .pen-bar for the
  // enhancements total), so the bubble measures whichever is actually fixed
  // and on screen, and lifts to clear it. Desktop renders those bars in flow,
  // where they cannot overlap, so nothing lifts.
  const [stickyLift, setStickyLift] = useState(0);
  useEffect(() => {
    const SEL = '.cvm-bar, .pen-bar, [data-sticky-bar="true"]';
    let frame = 0;
    const measure = () => {
      let h = 0;
      document.querySelectorAll(SEL).forEach((el) => {
        const cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') return;
        h = Math.max(h, el.getBoundingClientRect().height);
      });
      setStickyLift(h > 0 ? Math.round(h + 16) : 0);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    schedule();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, []);
  const floatBottom = `calc(${stickyLift || 64}px + env(safe-area-inset-bottom, 0px))`;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed right-4 sm:right-6 z-50 flex items-center gap-2 p-3.5 sm:px-5 sm:py-3 rounded-full sm:rounded-2xl text-[12px] font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          style={{
            bottom: floatBottom,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.06))',
            border: '1px solid rgba(212,175,55,0.25)',
            color: '#D4AF37',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
          data-testid="contact-admin-btn"
        >
          <div className="relative">
            <MessageCircle className="w-4 h-4" />
            {/* Unread COUNT badge — the actual "new message" signal. Gold
                circle with the number, sits on the icon so it reads at a
                glance and works whether the label is visible or hidden. */}
            {unreadCount > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9.5px] font-bold leading-none tabular-nums"
                style={{
                  background: 'linear-gradient(180deg, #F2D989 0%, #D4AF37 55%, #B88B1A 100%)',
                  color: '#0B0F1A',
                  border: '1.5px solid #0B0F1A',
                  boxShadow: '0 2px 8px rgba(212,175,55,0.45)',
                }}
                data-testid="contact-admin-unread-badge"
                aria-label={`${unreadCount} new message${unreadCount > 1 ? 's' : ''}`}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <span className="hidden sm:inline">Contact Us Directly</span>
        </button>
      )}

      {/* Message panel */}
      {open && (
        <div
          className="fixed right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] max-h-[520px] rounded-2xl overflow-hidden flex flex-col"
          style={{
            bottom: floatBottom,
            background: 'linear-gradient(180deg, #0E1220, #0A0E1A)',
            border: '1px solid rgba(212,175,55,0.12)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}
          data-testid="message-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#D4AF37]/50" />
              <span className="text-[13px] font-semibold text-white/70">Contact Us Directly</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-lg text-white/20 hover:text-white/40 transition"><X className="w-4 h-4" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px] max-h-[320px]" data-testid="message-thread">
            {loading && messages.length === 0 && (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-4 h-4 text-white/15 animate-spin" /></div>
            )}
            {!loading && messages.length === 0 && (
              <div className="text-center py-8">
                <p className="text-[12px] text-white/20">No messages yet</p>
                <p className="text-[10px] text-white/10 mt-1">Send a question and we&apos;ll respond as soon as possible.</p>
              </div>
            )}
            {messages.map(msg => {
              const isClient = msg.sender === 'client';
              return (
                <div key={msg.messageId} className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="max-w-[80%] rounded-xl px-3.5 py-2.5"
                    style={{
                      background: isClient ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isClient ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.06)'}`,
                    }}
                  >
                    {msg.topic && isClient && (
                      <span className="inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase tracking-wider mb-1"
                        style={{ background: 'rgba(255,255,255,0.04)', color: TOPICS.find(t => t.key === msg.topic)?.color || '#6B7280' }}>
                        {msg.topic}
                      </span>
                    )}
                    <p className="text-[12px] text-white/60 leading-relaxed">{msg.message}</p>
                    <p className="text-[9px] text-white/15 mt-1.5 text-right">{fmtTime(msg.createdAt)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Input */}
          <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            {/* Topic tags */}
            <div className="flex gap-1.5 mb-2">
              {TOPICS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTopic(t.key)}
                  className="px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider transition-all"
                  style={{
                    background: topic === t.key ? `${t.color}15` : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${topic === t.key ? `${t.color}30` : 'rgba(255,255,255,0.05)'}`,
                    color: topic === t.key ? t.color : 'rgba(255,255,255,0.2)',
                  }}
                  data-testid={`topic-${t.key.toLowerCase()}`}
                >
                  {t.key}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Type your question..."
                rows={1}
                className="flex-1 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3.5 py-2.5 text-[12px] text-white/70 placeholder:text-white/15 outline-none resize-none focus:border-[#D4AF37]/20"
                style={{ minHeight: '38px', maxHeight: '80px' }}
                data-testid="message-input"
              />
              <button
                onClick={handleSend}
                disabled={sending || !text.trim()}
                className="flex items-center justify-center w-9 h-9 rounded-xl transition-all disabled:opacity-25"
                style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 text-[#D4AF37] animate-spin" /> : <Send className="w-3.5 h-3.5 text-[#D4AF37]/70" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
